"use client";

import { Card, CardBody } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslations } from "next-intl";
import PageLayout from "../layout/PageLayout";

interface SectionProps {
  icon: string;
  iconColor: string;
  title: string;
  children: React.ReactNode;
}

function Section({ icon, iconColor, title, children }: SectionProps) {
  return (
    <Card className="bg-content1/80 border border-default-200/30">
      <CardBody className="p-5 md:p-6">
        <div className="flex items-start gap-4">
          <div
            className={`flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br ${iconColor} flex items-center justify-center`}
          >
            <Icon icon={icon} className="text-xl text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-lg text-[var(--text-primary)] mb-3">
              {title}
            </h2>
            <div className="text-default-600 text-sm md:text-base leading-relaxed space-y-3">
              {children}
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

export function PrivacyPage() {
  const t = useTranslations("privacy");

  return (
    <PageLayout
      pageHeaderProps={{
        pretitle: t("pretitle"),
        title: t("title"),
      }}
    >
      <div className="flex flex-col gap-6 w-full max-w-4xl pb-16">
        <p className="text-default-500 text-sm text-center mb-4">
          {t("lastUpdated")}
        </p>

        <Section
          icon="solar:shield-user-bold"
          iconColor="from-blue-500 to-blue-600"
          title={t("controller.title")}
        >
          <p>{t("controller.content")}</p>
          <p className="font-medium">{t("controller.contact")}</p>
        </Section>

        <Section
          icon="solar:database-bold"
          iconColor="from-emerald-500 to-emerald-600"
          title={t("dataCollected.title")}
        >
          <p>{t("dataCollected.intro")}</p>
          <ul className="list-disc list-inside space-y-2 ml-1">
            <li>
              <span className="font-medium">{t("dataCollected.name")}</span> -{" "}
              {t("dataCollected.nameDesc")}
            </li>
            <li>
              <span className="font-medium">{t("dataCollected.email")}</span> -{" "}
              {t("dataCollected.emailDesc")}
            </li>
          </ul>
        </Section>

        <Section
          icon="solar:document-text-bold"
          iconColor="from-violet-500 to-violet-600"
          title={t("purpose.title")}
        >
          <p>{t("purpose.intro")}</p>
          <ul className="list-disc list-inside space-y-2 ml-1">
            <li>{t("purpose.service")}</li>
            <li>{t("purpose.communication")}</li>
            <li>{t("purpose.notifications")}</li>
          </ul>
          <p className="mt-3">
            <span className="font-medium">{t("purpose.legalBasis")}</span>{" "}
            {t("purpose.legalBasisDesc")}
          </p>
        </Section>

        <Section
          icon="solar:letter-bold"
          iconColor="from-pink-500 to-pink-600"
          title={t("marketing.title")}
        >
          <p>{t("marketing.content")}</p>
          <p className="mt-3">
            <span className="font-medium">{t("marketing.legalBasis")}</span>{" "}
            {t("marketing.legalBasisDesc")}
          </p>
          <p className="mt-3">{t("marketing.optOut")}</p>
        </Section>

        <Section
          icon="solar:lock-bold"
          iconColor="from-amber-500 to-amber-600"
          title={t("protection.title")}
        >
          <p>{t("protection.content")}</p>
        </Section>

        <Section
          icon="solar:clock-circle-bold"
          iconColor="from-rose-500 to-rose-600"
          title={t("retention.title")}
        >
          <p>{t("retention.content")}</p>
        </Section>

        <Section
          icon="solar:users-group-rounded-bold"
          iconColor="from-cyan-500 to-cyan-600"
          title={t("sharing.title")}
        >
          <p>{t("sharing.content")}</p>
        </Section>

        <Section
          icon="solar:user-check-bold"
          iconColor="from-indigo-500 to-indigo-600"
          title={t("rights.title")}
        >
          <p>{t("rights.intro")}</p>
          <ul className="list-disc list-inside space-y-2 ml-1">
            <li>{t("rights.access")}</li>
            <li>{t("rights.rectify")}</li>
            <li>{t("rights.delete")}</li>
            <li>{t("rights.restrict")}</li>
            <li>{t("rights.object")}</li>
            <li>{t("rights.withdraw")}</li>
            <li>{t("rights.complaint")}</li>
          </ul>
          <p className="mt-3 font-medium">{t("rights.deleteNote")}</p>
          <p className="mt-3">{t("rights.contact")}</p>
        </Section>

        <Section
          icon="solar:refresh-circle-bold"
          iconColor="from-teal-500 to-teal-600"
          title={t("changes.title")}
        >
          <p>{t("changes.content")}</p>
        </Section>
      </div>
    </PageLayout>
  );
}
